import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button, Tag, Space, Modal, Form, Input, Select, Switch, message,
  Dropdown, Drawer, Descriptions, Typography, Alert, DatePicker,
} from "antd";
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  PlusOutlined, MoreOutlined, EyeOutlined, LinkOutlined, SyncOutlined, PaperClipOutlined, ToolOutlined, CheckCircleOutlined, EditOutlined, RollbackOutlined, SendOutlined, DownloadOutlined, FileSearchOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import fileDownload from "js-file-download";
import FilterBar, { FilterField } from "components/Common/FilterBar";
import useUrlFilters from "hooks/useUrlFilters";

import {
  getSubscriptions, getSubscription, subscribe, upgradeSubscription, upgradeInPlace,
  downgradeSubscription, quoteSubscriptionChange, cancelSubscription, resumeSubscription, renewSubscription,
  getPlans, applyOffer, getAvailableOffers, errorMessage, SUBSCRIPTION_STATUS_COLORS,
  createManualSubscription, activateManualSubscription, attachInvoice, detachInvoice,
  linkExternal, resyncSubscription, getSubscriptionInvoices, getSubscriptionPayments,
  getSubscriptionHistory, adjustSubscription,
  refundSubscription, getSubscriptionRefunds, recordGatewayPayment,
  getSubscriptionDocuments, sendDebitNotePaymentLink,
  attachCreditNote, detachCreditNote, attachDebitNote, detachDebitNote, downloadDocumentPdf,
  attachPayment, detachPayment,
  cancelGatewaySubscription, createGatewaySubscription, attachGatewaySubscription,
} from "./billingApi";

const REFUND_STATUS_COLORS = { PROCESSED: "green", PENDING: "orange", FAILED: "red" };

const { Text, Link } = Typography;

const fmtDate = (d) => (d ? dayjs(d).format("DD MMM YYYY") : "—");

// Manual-edit status options, derived from the shared status colour map.
const STATUS_OPTIONS = Object.keys(SUBSCRIPTION_STATUS_COLORS).map((s) => ({
  value: s,
  label: s.charAt(0).toUpperCase() + s.slice(1),
}));

const URL_FILTERS = {
  search: { type: "string" },
  status: { type: "string" },
  page: { type: "number", default: 1 },
  pageSize: { type: "number", key: "page_size", default: 50 },
};

const BillingSubscriptions = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  // Search, status and page live in the URL so the list can be shared.
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });
  const page = filters.page;
  const setPage = urlSetter("page");
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const search = filters.search || "";
  const setSearch = urlSetter("search");
  const [searchLive, setSearchLive] = useState(search);
  useEffect(() => setSearchLive(search), [search]);
  const statusFilter = filters.status || undefined;
  const setStatusFilter = urlSetter("status");

  const [plans, setPlans] = useState([]);
  const [offers, setOffers] = useState([]);

  // subscribe modal
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeForm] = Form.useForm();
  const [offerPreview, setOfferPreview] = useState(null);
  const [checkoutResult, setCheckoutResult] = useState(null);

  // plan-change modal (upgrade/downgrade)
  const [changeModal, setChangeModal] = useState(null); // {mode, record}
  const [changeForm] = Form.useForm();
  const [changing, setChanging] = useState(false);
  const [changeCheckout, setChangeCheckout] = useState(null); // {provider, short_url, ...}
  const [changeSettlement, setChangeSettlement] = useState(null); // {net, credit_note, debit_note, ...}
  const [changeQuote, setChangeQuote] = useState(null); // {quote, prorated, current_plan, new_plan}
  const [changeQuoteLoading, setChangeQuoteLoading] = useState(false);

  // detail drawer
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailInvoices, setDetailInvoices] = useState([]);
  const [detailInvoicesLoading, setDetailInvoicesLoading] = useState(false);
  const [detailPayments, setDetailPayments] = useState([]);
  const [detailPaymentsLoading, setDetailPaymentsLoading] = useState(false);
  const [detailHistory, setDetailHistory] = useState([]);
  const [detailHistoryLoading, setDetailHistoryLoading] = useState(false);
  const [detailResyncing, setDetailResyncing] = useState(false);
  // manual edit of status / start / renewal date (within the detail drawer)
  const [editModal, setEditModal] = useState(null); // subscription being edited
  const [editForm] = Form.useForm();
  const [editSaving, setEditSaving] = useState(false);
  // in-place upgrade (keep subscription, debit note for the difference)
  const [inPlaceModal, setInPlaceModal] = useState(null); // {record}
  const [inPlaceForm] = Form.useForm();
  const [inPlaceSubmitting, setInPlaceSubmitting] = useState(false);
  const [inPlaceResult, setInPlaceResult] = useState(null); // {debit_note, quote, checkout}
  // documents timeline + refunds (within the detail drawer)
  const [detailDocuments, setDetailDocuments] = useState([]);
  const [detailDocumentsLoading, setDetailDocumentsLoading] = useState(false);
  const [detailRefunds, setDetailRefunds] = useState([]);
  const [detailRefundsLoading, setDetailRefundsLoading] = useState(false);
  const [refundModal, setRefundModal] = useState(null); // subscription being refunded
  const [refundForm] = Form.useForm();
  const [refundSaving, setRefundSaving] = useState(false);
  // record an out-of-band gateway payment by id
  const [recordPayModal, setRecordPayModal] = useState(null); // {record}
  const [recordPayForm] = Form.useForm();
  const [recordPaySaving, setRecordPaySaving] = useState(false);
  // attach a credit/debit note by id
  const [noteAttach, setNoteAttach] = useState(null); // { type: "credit_note" | "debit_note" }
  const [noteAttachForm] = Form.useForm();
  const [noteAttaching, setNoteAttaching] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState(null);
  // attach a payment by id (to one of the subscription's invoices)
  const [paymentAttachOpen, setPaymentAttachOpen] = useState(false);
  const [paymentAttachForm] = Form.useForm();
  const [paymentAttaching, setPaymentAttaching] = useState(false);
  // manual gateway-subscription management (within the detail drawer)
  const [gwCreateModal, setGwCreateModal] = useState(null); // {} | {checkout}
  const [gwCreateForm] = Form.useForm();
  const [gwCreating, setGwCreating] = useState(false);
  const [gwAttachModal, setGwAttachModal] = useState(null); // {}
  const [gwAttachForm] = Form.useForm();
  const [gwAttaching, setGwAttaching] = useState(false);
  const [gwCancelingId, setGwCancelingId] = useState(null);

  // manual reconciliation modals
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSubmitting, setManualSubmitting] = useState(false);
  const [manualForm] = Form.useForm();
  const [linkModal, setLinkModal] = useState(null); // {record}
  const [linkForm] = Form.useForm();
  const [attachModal, setAttachModal] = useState(null); // {record}
  const [attachForm] = Form.useForm();
  const [activateModal, setActivateModal] = useState(null); // {record}
  const [activateForm] = Form.useForm();
  const [reconciling, setReconciling] = useState(false);

  // cancel modal (choose when: cycle end vs immediate)
  const [cancelModal, setCancelModal] = useState(null); // {record}
  const [cancelForm] = Form.useForm();
  const [canceling, setCanceling] = useState(false);

  const [actioningId, setActioningId] = useState(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = { page, page_size: pageSize };
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    getSubscriptions(params)
      .then((res) => {
        setData(res.data?.results || res.data || []);
        setTotal(res.data?.count ?? (res.data?.results || res.data || []).length);
      })
      .catch((err) => message.error(errorMessage(err, "Could not load subscriptions.")))
      .finally(() => setLoading(false));
  }, [page, pageSize, search, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    getPlans()
      .then((res) => setPlans(res.data?.results || res.data || []))
      .catch(() => {});
    getAvailableOffers()
      .then((res) => setOffers(res.data?.results || res.data || []))
      .catch(() => {});
  }, []);

  const planOptions = plans.map((p) => ({
    value: p.slug,
    label: `${p.product_code ? `${p.product_code} · ` : ""}${p.name} — ${p.currency} ${p.price}/${p.billing_cycle}`,
    raw: p,
  }));

  const offerOptions = offers.map((o) => ({
    value: o.code,
    label: `${o.code} — ${o.title}`,
  }));

  // --- subscribe ---
  // Preview the currently selected offer code (validates + shows discounted total).
  const handlePreviewOffer = async (codeArg) => {
    const code = codeArg ?? subscribeForm.getFieldValue("offer_code");
    const planSlug = subscribeForm.getFieldValue("plan");
    if (!code) {
      setOfferPreview(null);
      return;
    }
    try {
      const res = await applyOffer({ code, ...(planSlug ? { plan: planSlug } : {}) });
      setOfferPreview(res.data);
      message.success("Coupon is valid.");
    } catch (err) {
      setOfferPreview(null);
      message.error(errorMessage(err, "Invalid coupon."));
    }
  };

  const handleSubscribe = async () => {
    let values;
    try {
      values = await subscribeForm.validateFields();
    } catch {
      return;
    }
    setSubscribing(true);
    try {
      const res = await subscribe({
        plan: values.plan,
        customer_email: values.customer_email,
        customer_name: values.customer_name,
        customer_phone: values.customer_phone,
        provider: values.provider || "RAZORPAY",
        offer_code: values.offer_code || "",
        external_reference_id: values.external_reference_id || "",
        start_trial: values.start_trial || false,
      });
      setCheckoutResult(res.data?.checkout || null);
      message.success("Subscription created.");
      subscribeForm.resetFields();
      setOfferPreview(null);
      fetchData();
    } catch (err) {
      message.error(errorMessage(err, "Could not create subscription."));
    } finally {
      setSubscribing(false);
    }
  };

  // --- plan change ---
  const openChange = (mode, record) => {
    setChangeCheckout(null);
    setChangeSettlement(null);
    setChangeQuote(null);
    setChangeModal({ mode, record });
    changeForm.resetFields();
  };

  // Preview the amount due for the picked plan (prorated for a mid-cycle upgrade).
  const fetchChangeQuote = async () => {
    if (!changeModal) return;
    const plan = changeForm.getFieldValue("plan");
    if (!plan) { setChangeQuote(null); return; }
    setChangeQuoteLoading(true);
    try {
      const res = await quoteSubscriptionChange(changeModal.record.id, {
        plan,
        prorate: changeModal.mode === "upgrade" ? (changeForm.getFieldValue("prorate") ?? true) : false,
      });
      setChangeQuote(res.data);
    } catch {
      setChangeQuote(null);
    } finally {
      setChangeQuoteLoading(false);
    }
  };

  const handleChange = async () => {
    let values;
    try {
      values = await changeForm.validateFields();
    } catch {
      return;
    }
    const { mode, record } = changeModal;
    setChanging(true);
    try {
      let res;
      if (mode === "upgrade") {
        res = await upgradeSubscription(record.id, {
          plan: values.plan, prorate: values.prorate ?? true, provider: values.provider || undefined,
        });
      } else {
        res = await downgradeSubscription(record.id, {
          plan: values.plan, when: values.when || "cycle_end", provider: values.provider || undefined,
        });
      }
      const checkout = res?.data?.checkout || null;
      const settlement = res?.data?.settlement || null;
      fetchData();
      // An immediate downgrade returns a settlement (refund/credit note or debit
      // note) — keep the modal open to show it. Otherwise close on success.
      if (settlement) {
        setChangeSettlement(settlement);
        if (checkout?.short_url) setChangeCheckout({ ...checkout, mode });
        message.success("Downgrade applied.");
      } else if (checkout && checkout.short_url) {
        setChangeCheckout({ ...checkout, mode });
        message.success(`Subscription ${mode} applied — share the new authorisation link.`);
      } else {
        message.success(`Subscription ${mode} applied.`);
        setChangeModal(null);
      }
    } catch (err) {
      message.error(errorMessage(err, `Could not ${mode}.`));
    } finally {
      setChanging(false);
    }
  };

  // --- simple actions ---
  const runAction = async (fn, record, okMsg) => {
    setActioningId(record.id);
    try {
      const res = await fn();
      message.success(typeof okMsg === "function" ? okMsg(res?.data) : okMsg);
      fetchData();
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setActioningId(null);
    }
  };

  const loadDetailInvoices = async (id) => {
    setDetailInvoicesLoading(true);
    try {
      const res = await getSubscriptionInvoices(id);
      setDetailInvoices(res.data || []);
    } catch {
      setDetailInvoices([]);
    } finally {
      setDetailInvoicesLoading(false);
    }
  };

  const loadDetailHistory = async (id) => {
    setDetailHistoryLoading(true);
    try {
      const res = await getSubscriptionHistory(id);
      setDetailHistory(res.data || []);
    } catch {
      setDetailHistory([]);
    } finally {
      setDetailHistoryLoading(false);
    }
  };

  const loadDetailPayments = async (id) => {
    setDetailPaymentsLoading(true);
    try {
      const res = await getSubscriptionPayments(id);
      setDetailPayments(res.data || []);
    } catch {
      setDetailPayments([]);
    } finally {
      setDetailPaymentsLoading(false);
    }
  };

  const loadDetailRefunds = async (id) => {
    setDetailRefundsLoading(true);
    try {
      const res = await getSubscriptionRefunds(id);
      setDetailRefunds(res.data || []);
    } catch {
      setDetailRefunds([]);
    } finally {
      setDetailRefundsLoading(false);
    }
  };

  const loadDetailDocuments = async (id) => {
    setDetailDocumentsLoading(true);
    try {
      const res = await getSubscriptionDocuments(id);
      setDetailDocuments(res.data || []);
    } catch {
      setDetailDocuments([]);
    } finally {
      setDetailDocumentsLoading(false);
    }
  };

  // Generate + send a hosted payment link for a debit note, then show the link.
  const handleSendPaymentLink = async (doc) => {
    if (!detail?.id) return;
    setDownloadingDocId(doc.id);
    try {
      const res = await sendDebitNotePaymentLink(detail.id, doc.id, { notify: true });
      const url = res?.data?.short_url;
      Modal.success({
        title: "Payment link sent",
        content: url ? (
          <div>
            <p>The link was emailed/messaged to the customer. Share it directly if needed:</p>
            <a href={url} target="_blank" rel="noreferrer">{url}</a>
          </div>
        ) : "The payment link was created and sent to the customer.",
      });
      loadDetailDocuments(detail.id);
    } catch (err) {
      message.error(errorMessage(err, "Could not create the payment link."));
    } finally {
      setDownloadingDocId(null);
    }
  };

  const openDetail = async (record) => {
    setDetail({ id: record.id });
    setDetailLoading(true);
    setDetailInvoices([]);
    setDetailPayments([]);
    setDetailHistory([]);
    try {
      const res = await getSubscription(record.id);
      setDetail(res.data);
      loadDetailInvoices(record.id);
      loadDetailPayments(record.id);
      loadDetailRefunds(record.id);
      loadDetailDocuments(record.id);
      loadDetailHistory(record.id);
    } catch (err) {
      message.error(errorMessage(err));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Re-pull the open subscription detail (after a gateway-sub operation).
  const refreshDetail = async () => {
    if (!detail?.id) return;
    try {
      const res = await getSubscription(detail.id);
      setDetail(res.data);
      loadDetailInvoices(detail.id);
      loadDetailPayments(detail.id);
      loadDetailRefunds(detail.id);
      loadDetailDocuments(detail.id);
      loadDetailHistory(detail.id);
    } catch { /* keep current view on refresh error */ }
    fetchData();
  };

  // Open the refund modal for the currently open subscription.
  const openRefund = () => {
    if (!detail?.id) return;
    refundForm.resetFields();
    setRefundModal(detail);
  };

  const handleRefundSave = async () => {
    let values;
    try { values = await refundForm.validateFields(); } catch { return; }
    setRefundSaving(true);
    try {
      const res = await refundSubscription(refundModal.id, {
        amount: values.amount || null,
        reason: values.reason || "",
        cancel_subscription: values.cancel_subscription,
      });
      const cn = res?.data?.credit_note;
      message.success(cn ? `Refund recorded — credit note ${cn} issued.` : "Refund recorded.");
      setRefundModal(null);
      refundForm.resetFields();
      await refreshDetail();
    } catch (err) {
      message.error(errorMessage(err, "Could not process the refund."));
    } finally {
      setRefundSaving(false);
    }
  };

  // Record an out-of-band gateway payment (e.g. a link the operator sent) by id.
  const openRecordPayment = (record) => {
    recordPayForm.resetFields();
    setRecordPayModal({ record });
  };

  const handleRecordPayment = async () => {
    let values;
    try { values = await recordPayForm.validateFields(); } catch { return; }
    setRecordPaySaving(true);
    try {
      const res = await recordGatewayPayment(recordPayModal.record.id, {
        payment_id: values.payment_id?.trim(),
        amount: values.amount || null,
        mark_active: values.mark_active || false,
      });
      const inv = res?.data?.invoice;
      message.success(inv ? `Payment recorded against ${inv}.` : "Payment recorded.");
      setRecordPayModal(null);
      recordPayForm.resetFields();
      fetchData();
      if (detail?.id === recordPayModal.record.id) refreshDetail();
    } catch (err) {
      message.error(errorMessage(err, "Could not record the payment."));
    } finally {
      setRecordPaySaving(false);
    }
  };

  // In-place upgrade: keep the subscription, raise a debit note for the difference.
  const openInPlace = (record) => {
    inPlaceForm.resetFields();
    setInPlaceResult(null);
    setInPlaceModal({ record });
  };

  const handleInPlaceUpgrade = async () => {
    let values;
    try { values = await inPlaceForm.validateFields(); } catch { return; }
    setInPlaceSubmitting(true);
    try {
      const res = await upgradeInPlace(inPlaceModal.record.id, {
        plan: values.plan,
        offer_code: values.offer_code || "",
      });
      setInPlaceResult(res.data || {});
      const dn = res?.data?.debit_note;
      message.success(dn ? `Upgraded — debit note ${dn} raised.` : "Upgraded.");
      fetchData();
      if (detail?.id === inPlaceModal.record.id) refreshDetail();
    } catch (err) {
      message.error(errorMessage(err, "Could not upgrade the subscription."));
    } finally {
      setInPlaceSubmitting(false);
    }
  };

  // Open the manual-edit modal, pre-filled from the open subscription.
  const openEdit = () => {
    if (!detail?.id) return;
    editForm.setFieldsValue({
      status: detail.status,
      start_date: detail.start_date ? dayjs(detail.start_date) : null,
      renewal_date: detail.renewal_date ? dayjs(detail.renewal_date) : null,
    });
    setEditModal(detail);
  };

  const handleEditSave = async () => {
    let values;
    try { values = await editForm.validateFields(); } catch { return; }
    setEditSaving(true);
    try {
      await adjustSubscription(editModal.id, {
        status: values.status,
        start_date: values.start_date ? values.start_date.format("YYYY-MM-DD") : null,
        renewal_date: values.renewal_date ? values.renewal_date.format("YYYY-MM-DD") : null,
      });
      message.success("Subscription updated.");
      setEditModal(null);
      editForm.resetFields();
      await refreshDetail();
    } catch (err) {
      message.error(errorMessage(err, "Could not update subscription."));
    } finally {
      setEditSaving(false);
    }
  };

  // Hard fetch from the gateway for the open subscription: pull live status and
  // import any paid charges (e.g. a payment made while webhooks were down), then
  // refresh the drawer in place.
  const resyncDetail = async () => {
    if (!detail?.id) return;
    setDetailResyncing(true);
    try {
      const res = await resyncSubscription(detail.id);
      const d = res?.data || {};
      const bits = [];
      if (d.pulled) bits.push(`gateway status: ${d.pulled}`);
      if (d.payments_imported != null) bits.push(`${d.payments_imported} payment(s) imported`);
      message.success(`Fetched from gateway${bits.length ? " — " + bits.join(", ") : ""}.`);
      await refreshDetail();
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setDetailResyncing(false);
    }
  };

  // --- manual gateway-subscription management ---
  const handleGwCancel = (gs) => {
    if (!detail?.id) return;
    Modal.confirm({
      title: "Cancel this gateway subscription?",
      content: `This cancels ${gs.provider_subscription_id || "the gateway subscription"} at ${gs.provider} and marks it cancelled. The Teson subscription itself is not cancelled.`,
      okText: "Cancel gateway sub",
      okButtonProps: { danger: true },
      cancelText: "Keep",
      onOk: async () => {
        setGwCancelingId(gs.id);
        try {
          await cancelGatewaySubscription(detail.id, { gateway_subscription_id: gs.id });
          message.success("Gateway subscription cancelled.");
          await refreshDetail();
        } catch (err) {
          message.error(errorMessage(err, "Could not cancel gateway subscription."));
        } finally {
          setGwCancelingId(null);
        }
      },
    });
  };

  const handleGwCreate = async () => {
    let values;
    try { values = await gwCreateForm.validateFields(); } catch { return; }
    setGwCreating(true);
    try {
      const res = await createGatewaySubscription(detail.id, {
        provider: values.provider, plan: values.plan || undefined,
      });
      const checkout = res?.data?.checkout || null;
      await refreshDetail();
      if (checkout && checkout.short_url) {
        setGwCreateModal({ checkout });
        message.success("Gateway subscription created — share the authorisation link.");
      } else {
        message.success("Gateway subscription created.");
        setGwCreateModal(null);
      }
    } catch (err) {
      message.error(errorMessage(err, "Could not create gateway subscription."));
    } finally {
      setGwCreating(false);
    }
  };

  const handleGwAttach = async () => {
    let values;
    try { values = await gwAttachForm.validateFields(); } catch { return; }
    setGwAttaching(true);
    try {
      await attachGatewaySubscription(detail.id, {
        provider: values.provider,
        provider_subscription_id: values.provider_subscription_id,
        make_current: values.make_current ?? true,
      });
      message.success("Gateway subscription attached.");
      setGwAttachModal(null);
      await refreshDetail();
    } catch (err) {
      message.error(errorMessage(err, "Could not attach gateway subscription."));
    } finally {
      setGwAttaching(false);
    }
  };

  const handleCancel = async () => {
    let values;
    try { values = await cancelForm.validateFields(); } catch { return; }
    const { record } = cancelModal;
    const when = values.when || "cycle_end";
    setCanceling(true);
    try {
      await cancelSubscription(record.id, { when });
      message.success(when === "immediate" ? "Subscription cancelled immediately." : "Cancellation scheduled for cycle end.");
      setCancelModal(null);
      fetchData();
    } catch (err) {
      message.error(errorMessage(err, "Could not cancel subscription."));
    } finally {
      setCanceling(false);
    }
  };

  // --- manual reconciliation handlers ---
  const handleManualCreate = async () => {
    let values;
    try { values = await manualForm.validateFields(); } catch { return; }
    setManualSubmitting(true);
    try {
      await createManualSubscription({
        plan: values.plan,
        customer_email: values.customer_email,
        customer_name: values.customer_name,
        customer_phone: values.customer_phone,
        external_reference_id: values.external_reference_id || "",
        mark_active: values.mark_active || false,
      });
      message.success("Manual subscription created.");
      manualForm.resetFields();
      setManualOpen(false);
      fetchData();
    } catch (err) {
      message.error(errorMessage(err, "Could not create manual subscription."));
    } finally {
      setManualSubmitting(false);
    }
  };

  const handleLink = async () => {
    let values;
    try { values = await linkForm.validateFields(); } catch { return; }
    setReconciling(true);
    try {
      await linkExternal(linkModal.record.id, values.external_reference_id);
      message.success("External reference linked.");
      setLinkModal(null);
      linkForm.resetFields();
      fetchData();
    } catch (err) {
      message.error(errorMessage(err, "Could not link reference."));
    } finally {
      setReconciling(false);
    }
  };

  const handleAttach = async () => {
    let values;
    try { values = await attachForm.validateFields(); } catch { return; }
    setReconciling(true);
    try {
      await attachInvoice(attachModal.record.id, values.invoice_id);
      message.success("Invoice attached.");
      setAttachModal(null);
      attachForm.resetFields();
      if (detail?.id === attachModal.record.id) loadDetailInvoices(detail.id);
      fetchData();
    } catch (err) {
      message.error(errorMessage(err, "Could not attach invoice."));
    } finally {
      setReconciling(false);
    }
  };

  // --- document row actions (three-dot menu) ---
  const DOC_LABEL = { invoice: "invoice", credit_note: "credit note", debit_note: "debit note" };
  const DOC_VIEW_PATH = {
    invoice: (id) => `/invoices/edit/${id}`,
    credit_note: (id) => `/credit-notes/edit/${id}`,
    debit_note: (id) => `/debit-notes/edit/${id}`,
  };

  const handleDocDownload = async (type, id, number) => {
    setDownloadingDocId(id);
    try {
      const res = await downloadDocumentPdf(type, id);
      fileDownload(res.data, `${number || id}.pdf`);
    } catch (err) {
      message.error(errorMessage(err, "Could not download the document."));
    } finally {
      setDownloadingDocId(null);
    }
  };

  const handleDocDetach = (type, id, number) => {
    const label = DOC_LABEL[type];
    const detachFn = { invoice: detachInvoice, credit_note: detachCreditNote, debit_note: detachDebitNote }[type];
    Modal.confirm({
      title: `Detach this ${label}?`,
      content: `${number} will be unlinked from this subscription. The ${label} itself is kept.`,
      okText: "Detach",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await detachFn(detail.id, id);
          message.success(`${label[0].toUpperCase()}${label.slice(1)} detached.`);
          refreshDetail();
        } catch (err) {
          message.error(errorMessage(err, `Could not detach the ${label}.`));
        }
      },
    });
  };

  // Build the three-dot menu for a document row (invoice / credit_note / debit_note).
  const docMenu = ({ type, id, number, invoiceId, payable }) => {
    const items = [];
    if (type !== "invoice" && invoiceId) items.push({ key: "invoice", icon: <FileSearchOutlined />, label: "Go to Invoice" });
    items.push({ key: "view", icon: <EyeOutlined />, label: "View" });
    items.push({ key: "download", icon: <DownloadOutlined />, label: "Download" });
    if (type === "debit_note" && payable) items.push({ key: "sendlink", icon: <SendOutlined />, label: "Send payment link" });
    items.push({ type: "divider" });
    items.push({ key: "detach", danger: true, icon: <LinkOutlined />, label: "Detach" });
    const onClick = ({ key, domEvent }) => {
      domEvent?.stopPropagation?.();
      if (key === "invoice") return navigate(DOC_VIEW_PATH.invoice(invoiceId));
      if (key === "view") return navigate(DOC_VIEW_PATH[type](id));
      if (key === "download") return handleDocDownload(type, id, number);
      if (key === "sendlink") return handleSendPaymentLink({ id, payable });
      if (key === "detach") return handleDocDetach(type, id, number);
    };
    return { items, onClick };
  };

  // Render a three-dot menu cell (hidden when the menu has no items).
  const menuCell = (items, onClick, id) => {
    if (!items.length) return "—";
    return (
      <Dropdown menu={{ items, onClick }} trigger={["click"]} placement="bottomRight">
        <Button type="text" size="small" icon={<MoreOutlined />} loading={downloadingDocId === id} />
      </Dropdown>
    );
  };

  const docActionCell = (opts) => {
    const { items, onClick } = docMenu(opts);
    return menuCell(items, onClick, opts.id);
  };

  const handlePaymentAttach = async () => {
    let values;
    try { values = await paymentAttachForm.validateFields(); } catch { return; }
    setPaymentAttaching(true);
    try {
      await attachPayment(detail.id, {
        payment_id: values.payment_id?.trim(),
        invoice_id: values.invoice_id,
      });
      message.success("Payment attached.");
      setPaymentAttachOpen(false);
      paymentAttachForm.resetFields();
      refreshDetail();
    } catch (err) {
      message.error(errorMessage(err, "Could not attach the payment."));
    } finally {
      setPaymentAttaching(false);
    }
  };

  const handlePaymentDetach = (p) => {
    Modal.confirm({
      title: "Detach this payment?",
      content: `${p.receipt_no || "The payment"} will be unlinked from this subscription (the payment itself is kept).`,
      okText: "Detach",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await detachPayment(detail.id, p.id);
          message.success("Payment detached.");
          refreshDetail();
        } catch (err) {
          message.error(errorMessage(err, "Could not detach the payment."));
        }
      },
    });
  };

  // Payments menu: go to the invoice, download the receipt, detach.
  const paymentActionCell = (p) => {
    const items = [];
    if (p.invoice_id) items.push({ key: "invoice", icon: <FileSearchOutlined />, label: "Go to Invoice" });
    items.push({ key: "download", icon: <DownloadOutlined />, label: "Download receipt" });
    items.push({ type: "divider" });
    items.push({ key: "detach", danger: true, icon: <LinkOutlined />, label: "Detach" });
    const onClick = ({ key, domEvent }) => {
      domEvent?.stopPropagation?.();
      if (key === "invoice") return navigate(DOC_VIEW_PATH.invoice(p.invoice_id));
      if (key === "download") return handleDocDownload("payment", p.id, p.receipt_no);
      if (key === "detach") return handlePaymentDetach(p);
    };
    return menuCell(items, onClick, p.id);
  };

  // Refunds menu: jump to the linked credit note / invoice.
  const refundActionCell = (r) => {
    const items = [];
    if (r.credit_note_id) items.push({ key: "cn", icon: <FileSearchOutlined />, label: "Go to Credit Note" });
    if (r.invoice_id) items.push({ key: "invoice", icon: <FileSearchOutlined />, label: "Go to Invoice" });
    const onClick = ({ key, domEvent }) => {
      domEvent?.stopPropagation?.();
      if (key === "cn") return navigate(DOC_VIEW_PATH.credit_note(r.credit_note_id));
      if (key === "invoice") return navigate(DOC_VIEW_PATH.invoice(r.invoice_id));
    };
    return menuCell(items, onClick, r.id);
  };

  const handleNoteAttach = async () => {
    let values;
    try { values = await noteAttachForm.validateFields(); } catch { return; }
    const type = noteAttach.type;
    const attachFn = type === "credit_note" ? attachCreditNote : attachDebitNote;
    setNoteAttaching(true);
    try {
      await attachFn(detail.id, values.note_id?.trim());
      message.success(`${DOC_LABEL[type][0].toUpperCase()}${DOC_LABEL[type].slice(1)} attached.`);
      setNoteAttach(null);
      noteAttachForm.resetFields();
      refreshDetail();
    } catch (err) {
      message.error(errorMessage(err, `Could not attach the ${DOC_LABEL[type]}.`));
    } finally {
      setNoteAttaching(false);
    }
  };

  const handleActivate = async () => {
    let values;
    try { values = await activateForm.validateFields(); } catch { return; }
    setReconciling(true);
    try {
      await activateManualSubscription(activateModal.record.id, {
        amount: values.amount,
        payment_reference: values.payment_reference || "",
        generate_invoice: values.generate_invoice ?? true,
      });
      message.success("Subscription activated.");
      setActivateModal(null);
      activateForm.resetFields();
      fetchData();
    } catch (err) {
      message.error(errorMessage(err, "Could not activate subscription."));
    } finally {
      setReconciling(false);
    }
  };

  const rowMenu = (record) => {
    const items = [
      { key: "view", icon: <EyeOutlined />, label: "View details" },
      { key: "upgrade", label: "Upgrade plan" },
      { key: "downgrade", label: "Downgrade plan" },
      { key: "renew", label: "Renew now" },
    ];
    if (record.status === "paused" || record.status === "grace" || record.cancelled_at) {
      items.push({ key: "resume", label: "Resume" });
    }
    // Manual/offline pending subscriptions can be activated once paid out-of-band.
    if (record.provider === "OFFLINE" && record.status === "pending") {
      items.push({ key: "activate", icon: <CheckCircleOutlined />, label: "Activate (confirm payment)" });
    }
    items.push({ type: "divider" });
    items.push({ key: "record_payment", icon: <CheckCircleOutlined />, label: "Record gateway payment" });
    items.push({ key: "attach", icon: <PaperClipOutlined />, label: "Attach invoice" });
    items.push({ key: "link", icon: <LinkOutlined />, label: "Link external reference" });
    items.push({ key: "resync", icon: <SyncOutlined />, label: "Resync / fetch payments" });
    if (record.status !== "cancelled" && record.status !== "expired") {
      items.push({ type: "divider" });
      items.push({ key: "cancel", danger: true, label: "Cancel" });
    }
    const onClick = ({ key }) => {
      if (key === "view") return openDetail(record);
      if (key === "upgrade") return openInPlace(record);
      if (key === "downgrade") return openChange("downgrade", record);
      if (key === "renew") {
        Modal.confirm({
          title: "Renew this subscription now?",
          content: "This advances the subscription by one billing cycle and records you as the operator who performed the renewal (tracked in subscription history).",
          okText: "Confirm renewal",
          cancelText: "Cancel",
          onOk: () => runAction(() => renewSubscription(record.id), record, "Renewed."),
        });
        return;
      }
      if (key === "resume") return runAction(() => resumeSubscription(record.id), record, "Resumed.");
      if (key === "record_payment") return openRecordPayment(record);
      if (key === "activate") { activateForm.resetFields(); return setActivateModal({ record }); }
      if (key === "attach") { attachForm.resetFields(); return setAttachModal({ record }); }
      if (key === "link") {
        linkForm.setFieldsValue({ external_reference_id: record.external_reference_id || "" });
        return setLinkModal({ record });
      }
      if (key === "resync") return runAction(
        () => resyncSubscription(record.id),
        record,
        (d) => {
          const bits = [];
          if (d?.pulled) bits.push(`gateway status: ${d.pulled}`);
          if (d?.payments_imported != null) bits.push(`${d.payments_imported} payment(s) imported`);
          return `Fetched from gateway${bits.length ? " — " + bits.join(", ") : ""}.`;
        }
      );
      if (key === "cancel") {
        cancelForm.resetFields();
        setCancelModal({ record });
      }
    };
    return { items, onClick };
  };

  const columns = [
    {
      title: "Customer",
      dataIndex: "customer_email",
      render: (email, r) => (
        <Space direction="vertical" size={0}>
          <Text strong>{r.customer_name || email}</Text>
          {r.customer_name && <Text type="secondary" style={{ fontSize: 12 }}>{email}</Text>}
        </Space>
      ),
    },
    { title: "Product", dataIndex: "product_name" },
    { title: "Plan", dataIndex: "plan_name" },
    {
      title: "Status",
      dataIndex: "status",
      render: (s) => <Tag color={SUBSCRIPTION_STATUS_COLORS[s] || "default"}>{String(s).toUpperCase()}</Tag>,
    },
    { title: "Cycle", dataIndex: "billing_cycle" },
    {
      title: "Renews",
      dataIndex: "renewal_date",
      render: (renewalDate, r) => {
        // Count down to trial end while in trial, otherwise to next renewal.
        const inTrial = r.status === "trial" && r.trial_end;
        const date = inTrial ? r.trial_end : renewalDate;
        // Only count down for live subscriptions, not cancelled/expired/incomplete.
        const countdown = ["trial", "active", "pending", "paused", "grace"].includes(r.status);
        if (!date) return "—";
        if (!countdown) return <Text style={{ fontSize: 12 }}>{fmtDate(date)}</Text>;
        const days = dayjs(date).startOf("day").diff(dayjs().startOf("day"), "day");
        const color = days < 7 ? "red" : days <= 15 ? "orange" : "green";
        const label = days < 0 ? `${Math.abs(days)}d overdue`
          : days === 0 ? "Today" : `${days}d left`;
        return (
          <Space direction="vertical" size={0}>
            <Text style={{ fontSize: 12 }}>{fmtDate(date)}</Text>
            <Tag color={days < 0 ? "red" : color} style={{ marginRight: 0 }}>
              {inTrial ? `Trial · ${label}` : label}
            </Tag>
          </Space>
        );
      },
    },
    {
      title: "Provider",
      dataIndex: "provider",
      render: (p) => <Tag>{p}</Tag>,
    },
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_, record) => (
        <Dropdown menu={rowMenu(record)} trigger={["click"]}>
          <Button type="text" icon={<MoreOutlined />} loading={actioningId === record.id} />
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <Space style={{ marginBottom: 16, width: "100%", justifyContent: "space-between" }}>
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <FilterBar
            searchValue={searchLive}
            onSearchChange={setSearchLive}
            onSearch={(v) => { setPage(1); setSearch(v); }}
            searchPlaceholder="Search email / name / reference"
            activeCount={statusFilter ? 1 : 0}
            onClear={() => { setPage(1); setStatusFilter(undefined); }}
            title="Filter Subscriptions"
            addLabel="Subscribe a customer"
            onAdd={() => { setCheckoutResult(null); setSubscribeOpen(true); }}
            onRefresh={fetchData}
          >
            <FilterField label="Status">
              <Select
                placeholder="Status"
                allowClear
                value={statusFilter}
                onChange={(v) => { setPage(1); setStatusFilter(v); }}
                options={Object.keys(SUBSCRIPTION_STATUS_COLORS).map((s) => ({ value: s, label: s }))}
              />
            </FilterField>
          </FilterBar>
        </div>
        <Space>
          <Button icon={<ToolOutlined />} onClick={() => { manualForm.resetFields(); setManualOpen(true); }}>
            Add manually
          </Button>
        </Space>
      </Space>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={data}
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => setFilters({ page: ps !== pageSize ? 1 : p, pageSize: ps }),
        }}
      />

      {/* Subscribe modal */}
      <Modal
        title="Subscribe a customer"
        open={subscribeOpen}
        onCancel={() => { setSubscribeOpen(false); setCheckoutResult(null); }}
        onOk={handleSubscribe}
        confirmLoading={subscribing}
        okText="Create subscription"
        destroyOnClose
        width={560}
        footer={checkoutResult
          ? [
              <Button
                key="done"
                type="primary"
                onClick={() => { setSubscribeOpen(false); setCheckoutResult(null); }}
              >
                Done
              </Button>,
            ]
          : undefined}
      >
        {checkoutResult ? (
          <Alert
            type="success"
            showIcon
            message="Subscription created"
            description={
              <Space direction="vertical">
                {checkoutResult.type === "trial" ? (
                  <Text>Trial active until {fmtDate(checkoutResult.trial_end)}.</Text>
                ) : (
                  <>
                    <Text>Share this checkout link with the customer to authorise payment:</Text>
                    {checkoutResult.short_url ? (
                      <Link href={checkoutResult.short_url} target="_blank">
                        <LinkOutlined /> {checkoutResult.short_url}
                      </Link>
                    ) : (
                      <Text type="secondary">No hosted link returned by provider.</Text>
                    )}
                  </>
                )}
              </Space>
            }
          />
        ) : (
          <Form form={subscribeForm} layout="vertical">
            <Form.Item name="plan" label="Plan" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label" options={planOptions} placeholder="Select a plan" />
            </Form.Item>
            <Form.Item name="customer_email" label="Customer email" rules={[{ required: true, type: "email" }]}>
              <Input placeholder="customer@example.com" />
            </Form.Item>
            <Form.Item name="customer_name" label="Customer name">
              <Input />
            </Form.Item>
            <Form.Item name="customer_phone" label="Customer phone">
              <Input />
            </Form.Item>
            <Form.Item name="external_reference_id" label="External reference (optional)">
              <Input placeholder="Reference from the originating product" />
            </Form.Item>
            <Form.Item name="offer_code" label="Offer (optional)">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={offerOptions.length ? "Select an offer" : "No offers available"}
                options={offerOptions}
                onChange={(val) => (val ? handlePreviewOffer(val) : setOfferPreview(null))}
              />
            </Form.Item>
            {offerPreview?.valid && (
              <Alert
                type="success"
                style={{ marginBottom: 16 }}
                message={`Coupon ${offerPreview.offer?.code || ""} applied`}
                description={offerPreview.quote ? `Total after discount: ${offerPreview.quote.currency} ${offerPreview.quote.total}` : null}
              />
            )}
            <Form.Item name="start_trial" label="Start as trial" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="provider" label="Provider" initialValue="RAZORPAY">
              <Select
                options={[
                  { value: "RAZORPAY", label: "Razorpay" },
                  { value: "CASHFREE", label: "Cashfree" },
                ]}
              />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Upgrade / downgrade modal */}
      <Modal
        title={changeModal?.mode === "upgrade" ? "Upgrade plan" : "Downgrade plan"}
        open={!!changeModal}
        onCancel={() => { setChangeModal(null); setChangeCheckout(null); setChangeSettlement(null); setChangeQuote(null); }}
        onOk={(changeCheckout || changeSettlement)
          ? () => { setChangeModal(null); setChangeCheckout(null); setChangeSettlement(null); setChangeQuote(null); }
          : handleChange}
        okText={(changeCheckout || changeSettlement) ? "Done" : "OK"}
        cancelButtonProps={(changeCheckout || changeSettlement) ? { style: { display: "none" } } : undefined}
        confirmLoading={changing}
        destroyOnClose
      >
        {changeSettlement ? (
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Alert
              type="success"
              showIcon
              message="Downgrade applied"
              description="The subscription moved to the new plan on a fresh term. The unused value of the old plan was settled against the new plan's price."
            />
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Unused credit">{changeSettlement.credit}</Descriptions.Item>
              <Descriptions.Item label="New plan price">{changeSettlement.new_price}</Descriptions.Item>
              {changeSettlement.credit_note ? (
                <Descriptions.Item label="Refunded + credit note">
                  {Math.abs(Number(changeSettlement.net)).toFixed(2)} · {changeSettlement.credit_note}
                </Descriptions.Item>
              ) : null}
              {changeSettlement.debit_note ? (
                <Descriptions.Item label="Debit note (customer owes)">
                  {Math.abs(Number(changeSettlement.net)).toFixed(2)} · {changeSettlement.debit_note}
                </Descriptions.Item>
              ) : null}
              {!changeSettlement.credit_note && !changeSettlement.debit_note ? (
                <Descriptions.Item label="Settlement">Nothing due either way</Descriptions.Item>
              ) : null}
            </Descriptions>
            {changeCheckout?.short_url ? (
              <div>
                <Text>Collect the amount owed:</Text>{" "}
                <Link href={changeCheckout.short_url} target="_blank">
                  <LinkOutlined /> {changeCheckout.short_url}
                </Link>
              </div>
            ) : null}
          </Space>
        ) : changeCheckout ? (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Alert
              type="success"
              showIcon
              message="Plan changed — new gateway subscription created"
              description={`The previous gateway subscription was cancelled and a new one was created on ${changeCheckout.provider}. The customer must re-authorise payment.`}
            />
            <Text>Share this authorisation link with the customer:</Text>
            <Link href={changeCheckout.short_url} target="_blank">
              <LinkOutlined /> {changeCheckout.short_url}
            </Link>
          </Space>
        ) : (
          <Form form={changeForm} layout="vertical">
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="At cycle end, the cheaper plan applies from the next renewal — no money moves now. Immediately starts a fresh term on the new plan now and settles the unused value: a surplus is refunded with a credit note, a shortfall is billed as a debit note."
            />
            <Form.Item name="plan" label="New plan" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label" options={planOptions}
                placeholder="Select target plan" onChange={fetchChangeQuote} />
            </Form.Item>
            <Form.Item
              name="provider"
              label="Gateway"
              initialValue={changeModal?.record?.provider || "RAZORPAY"}
              extra="Defaults to the current gateway. Pick another to switch providers."
            >
              <Select
                options={[
                  { value: "RAZORPAY", label: "Razorpay" },
                  { value: "CASHFREE", label: "Cashfree" },
                  { value: "OFFLINE", label: "Offline / Manual" },
                ]}
              />
            </Form.Item>
            {changeModal?.mode === "upgrade" ? (
              <Form.Item name="prorate" label="Prorate unused time" valuePropName="checked" initialValue={true}>
                <Switch onChange={fetchChangeQuote} />
              </Form.Item>
            ) : (
              <Form.Item name="when" label="When" initialValue="cycle_end">
                <Select
                  options={[
                    { value: "cycle_end", label: "At end of current cycle (recommended)" },
                    { value: "immediate", label: "Immediately" },
                  ]}
                />
              </Form.Item>
            )}

            {(changeQuoteLoading || changeQuote) && (
              <div style={{ marginTop: 8 }}>
                {changeQuoteLoading ? (
                  <Text type="secondary">Calculating amount…</Text>
                ) : (
                  <Descriptions column={1} size="small" bordered
                    title="Amount due"
                    styles={{ label: { width: 200 } }}>
                    <Descriptions.Item label="Current plan">
                      {changeQuote.current_plan?.name} — {changeQuote.new_plan?.currency} {changeQuote.current_plan?.price}
                    </Descriptions.Item>
                    <Descriptions.Item label="New plan">
                      {changeQuote.new_plan?.name} — {changeQuote.new_plan?.currency} {changeQuote.quote?.base}
                    </Descriptions.Item>
                    {changeQuote.prorated && Number(changeQuote.quote?.proration_credit) > 0 && (
                      <Descriptions.Item label="Unused-time credit">
                        − {changeQuote.new_plan?.currency} {changeQuote.quote?.proration_credit}
                      </Descriptions.Item>
                    )}
                    {Number(changeQuote.quote?.discount) > 0 && (
                      <Descriptions.Item label="Discount">
                        − {changeQuote.new_plan?.currency} {changeQuote.quote?.discount}
                      </Descriptions.Item>
                    )}
                    <Descriptions.Item label={<Text strong>Amount due now</Text>}>
                      <Text strong>{changeQuote.new_plan?.currency} {changeQuote.quote?.total}</Text>
                    </Descriptions.Item>
                  </Descriptions>
                )}
                {changeQuote && !changeQuoteLoading && (
                  <Text type="warning" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
                    Note: this is the prorated amount for reference. The Razorpay authorisation link
                    will still bill the full plan price ({changeQuote.new_plan?.currency} {changeQuote.quote?.base}) —
                    the unused-time credit is not applied at the gateway.
                  </Text>
                )}
              </div>
            )}
          </Form>
        )}
      </Modal>

      {/* Cancel subscription modal */}
      <Modal
        title="Cancel subscription"
        open={!!cancelModal}
        onCancel={() => setCancelModal(null)}
        onOk={handleCancel}
        okText="Cancel subscription"
        okButtonProps={{ danger: true }}
        cancelText="Keep"
        confirmLoading={canceling}
        destroyOnClose
      >
        <Form form={cancelForm} layout="vertical">
          <Form.Item name="when" label="When should it be cancelled?" initialValue="cycle_end">
            <Select
              options={[
                { value: "cycle_end", label: "At end of current cycle (customer keeps access until then)" },
                { value: "immediate", label: "Immediately (access ends now, gateway subscriptions cancelled now)" },
              ]}
            />
          </Form.Item>
          <Alert
            type="warning"
            showIcon
            message="Cancelling stops auto-renewal and cancels all linked gateway subscriptions. Immediate cancellation ends the subscription right away."
          />
        </Form>
      </Modal>

      {/* Create gateway subscription modal */}
      <Modal
        title="Create gateway subscription"
        open={!!gwCreateModal}
        onCancel={() => setGwCreateModal(null)}
        onOk={gwCreateModal?.checkout ? () => setGwCreateModal(null) : handleGwCreate}
        okText={gwCreateModal?.checkout ? "Done" : "Create"}
        cancelButtonProps={gwCreateModal?.checkout ? { style: { display: "none" } } : undefined}
        confirmLoading={gwCreating}
        destroyOnClose
      >
        {gwCreateModal?.checkout ? (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Alert type="success" showIcon
              message={`New gateway subscription created on ${gwCreateModal.checkout.provider}`}
              description="The customer must authorise payment via the link below." />
            <Link href={gwCreateModal.checkout.short_url} target="_blank">
              <LinkOutlined /> {gwCreateModal.checkout.short_url}
            </Link>
          </Space>
        ) : (
          <Form form={gwCreateForm} layout="vertical">
            <Alert type="info" showIcon style={{ marginBottom: 16 }}
              message="Creates a new gateway subscription and makes it the current instrument. Existing gateway subscriptions are kept (not cancelled) — cancel them separately if needed." />
            <Form.Item name="provider" label="Provider" initialValue={detail?.provider || "RAZORPAY"}
              rules={[{ required: true }]}>
              <Select options={[
                { value: "RAZORPAY", label: "Razorpay" },
                { value: "CASHFREE", label: "Cashfree" },
                { value: "OFFLINE", label: "Offline / Manual" },
              ]} />
            </Form.Item>
            <Form.Item name="plan" label="Plan" extra="Defaults to the subscription's current plan.">
              <Select allowClear showSearch optionFilterProp="label" options={planOptions}
                placeholder={detail?.plan_name ? `Current: ${detail.plan_name}` : "Current plan"} />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Attach gateway subscription modal */}
      <Modal
        title="Attach gateway subscription"
        open={!!gwAttachModal}
        onCancel={() => setGwAttachModal(null)}
        onOk={handleGwAttach}
        okText="Attach"
        confirmLoading={gwAttaching}
        destroyOnClose
      >
        <Form form={gwAttachForm} layout="vertical">
          <Alert type="info" showIcon style={{ marginBottom: 16 }}
            message="Links an existing provider subscription to this Teson subscription. Its status is pulled from the gateway when possible." />
          <Form.Item name="provider" label="Provider" initialValue={detail?.provider || "RAZORPAY"}
            rules={[{ required: true }]}>
            <Select options={[
              { value: "RAZORPAY", label: "Razorpay" },
              { value: "CASHFREE", label: "Cashfree" },
              { value: "OFFLINE", label: "Offline / Manual" },
            ]} />
          </Form.Item>
          <Form.Item name="provider_subscription_id" label="Provider subscription id"
            rules={[{ required: true }]}>
            <Input placeholder="e.g. sub_XXXXXXXXXXXX" />
          </Form.Item>
          <Form.Item name="make_current" label="Make this the current instrument"
            valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Detail drawer */}
      <Drawer
        title="Subscription details"
        open={!!detail}
        onClose={() => setDetail(null)}
        width="60%"
        loading={detailLoading}
        extra={
          detail && detail.id ? (
            <Space>
              <Button icon={<EditOutlined />} onClick={openEdit}>
                Edit
              </Button>
              {detail.status !== "cancelled" && detail.status !== "expired" && (
                <Button danger icon={<RollbackOutlined />} onClick={openRefund}>
                  Refund
                </Button>
              )}
              {detail.provider !== "OFFLINE" && (
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  loading={detailResyncing}
                  onClick={resyncDetail}
                >
                  Fetch payments
                </Button>
              )}
            </Space>
          ) : null
        }
      >
        {detail && detail.id && detail.status && (
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Customer">{detail.customer_name || detail.customer_email}</Descriptions.Item>
              <Descriptions.Item label="Email">{detail.customer_email}</Descriptions.Item>
              <Descriptions.Item label="Product">{detail.product_name}</Descriptions.Item>
              <Descriptions.Item label="Plan">{detail.plan_name}</Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={SUBSCRIPTION_STATUS_COLORS[detail.status] || "default"}>{String(detail.status).toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Provider">{detail.provider}</Descriptions.Item>
              <Descriptions.Item label="Cycle">{detail.billing_cycle}</Descriptions.Item>
              <Descriptions.Item label="Start">{fmtDate(detail.start_date)}</Descriptions.Item>
              <Descriptions.Item label="Trial ends">{fmtDate(detail.trial_end)}</Descriptions.Item>
              <Descriptions.Item label="Renews">{fmtDate(detail.renewal_date)}</Descriptions.Item>
              <Descriptions.Item label="Auto-renew">{detail.auto_renew ? "Yes" : "No"}</Descriptions.Item>
              <Descriptions.Item label="External reference">{detail.external_reference_id || "—"}</Descriptions.Item>
            </Descriptions>

            {(() => {
              const gws = [...(detail.gateway_subscriptions || [])].sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at));
              return (
                <div>
                  <Space style={{ justifyContent: "space-between", width: "100%", marginBottom: 8 }}>
                    <Text strong>Gateway Subscriptions ({gws.length})</Text>
                    <Space>
                      <Button size="small" icon={<PlusOutlined />}
                        onClick={() => { gwCreateForm.resetFields(); setGwCreateModal({}); }}>
                        Create
                      </Button>
                      <Button size="small" icon={<LinkOutlined />}
                        onClick={() => { gwAttachForm.resetFields(); setGwAttachModal({}); }}>
                        Attach
                      </Button>
                    </Space>
                  </Space>
                  <Table
                    rowKey="id"
                    size="small"
                    dataSource={gws}
                    pagination={false}
                    locale={{ emptyText: "No gateway subscriptions" }}
                    rowClassName={(r) => (r.is_current ? "ant-table-row-selected" : "")}
                    columns={[
                      {
                        title: "Provider sub id", dataIndex: "provider_subscription_id",
                        render: (v) => (v ? (
                          <Link onClick={() => navigate(`/billing/gateway?tab=subscriptions&sub=${encodeURIComponent(v)}`)}>
                            {v}
                          </Link>
                        ) : "—"),
                      },
                      { title: "Provider", dataIndex: "provider", render: (v) => <Tag color="blue">{v}</Tag> },
                      {
                        title: "Status", dataIndex: "status",
                        render: (v) => (v ? <Tag>{v}</Tag> : "—"),
                      },
                      {
                        title: "Active", dataIndex: "is_current",
                        render: (v) => (v ? <Tag color="green">Active</Tag> : <Tag>History</Tag>),
                      },
                      { title: "Reason", dataIndex: "reason", render: (v) => <Tag>{v}</Tag> },
                      { title: "Created", dataIndex: "created_at", render: fmtDate },
                      {
                        title: "", key: "act", width: 70,
                        render: (_, gs) => (
                          gs.status === "cancelled" ? null : (
                            <Button size="small" danger type="link"
                              loading={gwCancelingId === gs.id}
                              onClick={() => handleGwCancel(gs)}>
                              Cancel
                            </Button>
                          )
                        ),
                      },
                    ]}
                  />
                </div>
              );
            })()}

            <div>
              <Text strong>History ({detailHistory.length})</Text>
              <Table
                rowKey="history_id"
                size="small"
                style={{ marginTop: 8 }}
                loading={detailHistoryLoading}
                dataSource={detailHistory}
                pagination={{ defaultPageSize: 8, showSizeChanger: true, hideOnSinglePage: true }}
                locale={{ emptyText: "No history" }}
                columns={[
                  { title: "When", dataIndex: "date", render: (v) => (v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—") },
                  { title: "User", dataIndex: "user", render: (v) => v || "system" },
                  {
                    title: "Change", dataIndex: "reason",
                    render: (v, r) => v || <Tag>{r.type}</Tag>,
                  },
                  {
                    title: "Status", dataIndex: "status",
                    render: (v) => (v ? <Tag color={SUBSCRIPTION_STATUS_COLORS[v] || "default"}>{String(v).toUpperCase()}</Tag> : "—"),
                  },
                  { title: "Plan", dataIndex: "plan", render: (v) => v || "—" },
                ]}
              />
            </div>

            {detailDocuments.some((d) => d.type === "debit_note") && (
            <div>
              <Space style={{ justifyContent: "space-between", width: "100%", marginBottom: 8 }}>
                <Text strong>Debit Notes ({detailDocuments.filter((d) => d.type === "debit_note").length})</Text>
                <Button size="small" icon={<PaperClipOutlined />}
                  onClick={() => { noteAttachForm.resetFields(); setNoteAttach({ type: "debit_note" }); }}>
                  Attach debit note
                </Button>
              </Space>
              <Table
                rowKey="id"
                size="small"
                loading={detailDocumentsLoading}
                dataSource={detailDocuments.filter((d) => d.type === "debit_note")}
                pagination={{ defaultPageSize: 8, showSizeChanger: true, hideOnSinglePage: true }}
                columns={[
                  { title: "Debit Note #", dataIndex: "number", render: (v) => <Text style={{ fontSize: 12 }}>{v}</Text> },
                  { title: "Date", dataIndex: "date", render: fmtDate },
                  { title: "Amount", dataIndex: "amount", render: (v) => (v != null ? Number(v).toFixed(2) : "—") },
                  { title: "Status", dataIndex: "status", render: (v) => (v ? <Tag>{String(v).toUpperCase()}</Tag> : "—") },
                  { title: "Invoice", dataIndex: "invoice_number", render: (v) => v || "—" },
                  { title: "GP id", dataIndex: "gp_id", render: (v) => (v ? <Text type="secondary" style={{ fontSize: 11 }}>{v}</Text> : "—") },
                  {
                    title: "", key: "act", width: 40,
                    render: (_, d) => docActionCell({
                      type: "debit_note", id: d.id, number: d.number,
                      invoiceId: d.invoice_id, payable: d.payable,
                    }),
                  },
                ]}
              />
            </div>
            )}

            {detailDocuments.some((d) => d.type === "credit_note") && (
            <div>
              <Space style={{ justifyContent: "space-between", width: "100%", marginBottom: 8 }}>
                <Text strong>Credit Notes ({detailDocuments.filter((d) => d.type === "credit_note").length})</Text>
                <Button size="small" icon={<PaperClipOutlined />}
                  onClick={() => { noteAttachForm.resetFields(); setNoteAttach({ type: "credit_note" }); }}>
                  Attach credit note
                </Button>
              </Space>
              <Table
                rowKey="id"
                size="small"
                loading={detailDocumentsLoading}
                dataSource={detailDocuments.filter((d) => d.type === "credit_note")}
                pagination={{ defaultPageSize: 8, showSizeChanger: true, hideOnSinglePage: true }}
                columns={[
                  { title: "Credit Note #", dataIndex: "number", render: (v) => <Text style={{ fontSize: 12 }}>{v}</Text> },
                  { title: "Date", dataIndex: "date", render: fmtDate },
                  { title: "Amount", dataIndex: "amount", render: (v) => (v != null ? Number(v).toFixed(2) : "—") },
                  { title: "Status", dataIndex: "status", render: (v) => (v ? <Tag>{String(v).toUpperCase()}</Tag> : "—") },
                  { title: "Invoice", dataIndex: "invoice_number", render: (v) => v || "—" },
                  {
                    title: "", key: "act", width: 40,
                    render: (_, d) => docActionCell({
                      type: "credit_note", id: d.id, number: d.number, invoiceId: d.invoice_id,
                    }),
                  },
                ]}
              />
            </div>
            )}

            <div>
              <Space style={{ justifyContent: "space-between", width: "100%", marginBottom: 8 }}>
                <Text strong>Invoices ({detailInvoices.length})</Text>
                <Button
                  size="small"
                  icon={<PaperClipOutlined />}
                  onClick={() => { attachForm.resetFields(); setAttachModal({ record: detail }); }}
                >
                  Attach invoice
                </Button>
              </Space>
              <Table
                rowKey="id"
                size="small"
                loading={detailInvoicesLoading}
                dataSource={detailInvoices}
                pagination={false}
                locale={{ emptyText: "No invoices attached" }}
                columns={[
                  { title: "Invoice #", dataIndex: "inv_number" },
                  { title: "Date", dataIndex: "invoice_date", render: fmtDate },
                  { title: "Amount", dataIndex: "total" },
                  {
                    title: "Status", dataIndex: "status",
                    render: (s) => <Tag>{String(s).toUpperCase()}</Tag>,
                  },
                  { title: "GP id", dataIndex: "gp_id", render: (v) => (v ? <Text type="secondary" style={{ fontSize: 11 }}>{v}</Text> : "—") },
                  {
                    title: "", key: "x", width: 40,
                    render: (_, inv) => docActionCell({
                      type: "invoice", id: inv.id, number: inv.inv_number, invoiceId: inv.id,
                    }),
                  },
                ]}
              />
            </div>

            <div>
              <Space style={{ justifyContent: "space-between", width: "100%", marginBottom: 8 }}>
                <Text strong>Payments ({detailPayments.length})</Text>
                <Button size="small" icon={<PaperClipOutlined />}
                  onClick={() => { paymentAttachForm.resetFields(); setPaymentAttachOpen(true); }}>
                  Attach payment
                </Button>
              </Space>
              <Table
                rowKey="id"
                size="small"
                loading={detailPaymentsLoading}
                dataSource={detailPayments}
                pagination={{ defaultPageSize: 8, showSizeChanger: true, hideOnSinglePage: true }}
                locale={{ emptyText: "No payments recorded" }}
                columns={[
                  { title: "Payment #", dataIndex: "receipt_no", render: (v) => v || "—" },
                  { title: "Date", dataIndex: "payment_date", render: fmtDate },
                  { title: "Amount", dataIndex: "amount" },
                  { title: "Mode", dataIndex: "payment_mode", render: (v) => (v ? <Tag>{v}</Tag> : "—") },
                  {
                    title: "Status", dataIndex: "status",
                    render: (v) => <Tag color={v === "PAID" ? "green" : v === "FAILED" ? "red" : "default"}>{v}</Tag>,
                  },
                  { title: "Invoice", dataIndex: "invoice_number", render: (v) => v || "—" },
                  { title: "GP id", dataIndex: "transaction_id", render: (v) => (v ? <Text type="secondary" style={{ fontSize: 11 }}>{v}</Text> : "—") },
                  { title: "", key: "act", width: 40, render: (_, p) => paymentActionCell(p) },
                ]}
              />
            </div>

            <div>
              <Text strong>Refunds ({detailRefunds.length})</Text>
              <Table
                rowKey="id"
                size="small"
                style={{ marginTop: 8 }}
                loading={detailRefundsLoading}
                dataSource={detailRefunds}
                pagination={{ defaultPageSize: 8, showSizeChanger: true, hideOnSinglePage: true }}
                locale={{ emptyText: "No refunds" }}
                columns={[
                  { title: "Refund #", dataIndex: "rf_number", render: (v, r) => <Text style={{ fontSize: 12 }}>{v || String(r.id).slice(0, 8)}</Text> },
                  { title: "Date", dataIndex: "refund_date", render: fmtDate },
                  { title: "Amount", dataIndex: "amount" },
                  { title: "Provider", dataIndex: "provider", render: (v) => (v ? <Tag>{v}</Tag> : "—") },
                  {
                    title: "Status", dataIndex: "status",
                    render: (v) => <Tag color={REFUND_STATUS_COLORS[v] || "default"}>{v}</Tag>,
                  },
                  {
                    title: "Credit Note", dataIndex: "credit_note_number",
                    render: (v) => (v ? <Text style={{ fontSize: 12 }}>{v}</Text> : "—"),
                  },
                  { title: "GP id", dataIndex: "provider_refund_id", render: (v) => (v ? <Text type="secondary" style={{ fontSize: 11 }}>{v}</Text> : "—") },
                  { title: "", key: "act", width: 40, render: (_, r) => refundActionCell(r) },
                ]}
              />
            </div>

            {detail.entitlements && Object.keys(detail.entitlements).length > 0 && (
              <Descriptions title="Feature limits" column={1} size="small" bordered>
                {Object.entries(detail.entitlements).map(([k, v]) => (
                  <Descriptions.Item key={k} label={k}>{String(v)}</Descriptions.Item>
                ))}
              </Descriptions>
            )}
          </Space>
        )}
      </Drawer>

      {/* Manual / offline subscription create */}
      <Modal
        title="Add a subscription manually"
        open={manualOpen}
        onCancel={() => setManualOpen(false)}
        onOk={handleManualCreate}
        confirmLoading={manualSubmitting}
        okText="Create"
        destroyOnClose
        width={560}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="For offline payments or to recover a subscription that failed to sync. No payment gateway is contacted."
        />
        <Form form={manualForm} layout="vertical">
          <Form.Item name="plan" label="Plan" rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={planOptions} placeholder="Select a plan" />
          </Form.Item>
          <Form.Item name="customer_email" label="Customer email" rules={[{ required: true, type: "email" }]}>
            <Input placeholder="customer@example.com" />
          </Form.Item>
          <Form.Item name="customer_name" label="Customer name">
            <Input />
          </Form.Item>
          <Form.Item name="customer_phone" label="Customer phone">
            <Input />
          </Form.Item>
          <Form.Item name="external_reference_id" label="External reference (product subscription id)">
            <Input placeholder="Paste the product-side subscription id to link them" />
          </Form.Item>
          <Form.Item name="mark_active" label="Mark active immediately" valuePropName="checked" initialValue={false}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Link external reference */}
      <Modal
        title="Link external reference"
        open={!!linkModal}
        onCancel={() => setLinkModal(null)}
        onOk={handleLink}
        confirmLoading={reconciling}
        okText="Link"
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Reconcile a drifted pair: set the product-side subscription id so the two systems map to each other. Saving re-pushes the current state to the product."
        />
        <Form form={linkForm} layout="vertical">
          <Form.Item name="external_reference_id" label="External reference id" rules={[{ required: true }]}>
            <Input placeholder="Product subscription id" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Attach invoice */}
      <Modal
        title="Attach invoice"
        open={!!attachModal}
        onCancel={() => setAttachModal(null)}
        onOk={handleAttach}
        confirmLoading={reconciling}
        okText="Attach"
        destroyOnClose
      >
        <Form form={attachForm} layout="vertical">
          <Form.Item
            name="invoice_id"
            label="Invoice ID"
            rules={[{ required: true, message: "Enter the TesonCRM invoice UUID" }]}
          >
            <Input placeholder="TesonCRM invoice UUID" />
          </Form.Item>
          <Text type="secondary">
            The invoice must belong to the same organization. It will be linked to this
            subscription and reflected on the product.
          </Text>
        </Form>
      </Modal>

      {/* Activate manual subscription (confirm offline payment) */}
      <Modal
        title="Activate subscription"
        open={!!activateModal}
        onCancel={() => setActivateModal(null)}
        onOk={handleActivate}
        confirmLoading={reconciling}
        okText="Activate"
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Confirm an out-of-band payment. The subscription is opened active and (optionally) a PAID invoice + payment are recorded."
        />
        <Form form={activateForm} layout="vertical">
          <Form.Item name="amount" label="Amount paid (optional — defaults to plan price)">
            <Input type="number" min={0} placeholder="e.g. 1180.00" />
          </Form.Item>
          <Form.Item name="payment_reference" label="Payment reference (optional)">
            <Input placeholder="Bank UTR / cheque no / txn id" />
          </Form.Item>
          <Form.Item name="generate_invoice" label="Generate a PAID invoice" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Manual edit of status / start / renewal date */}
      <Modal
        title="Edit subscription"
        open={!!editModal}
        onCancel={() => setEditModal(null)}
        onOk={handleEditSave}
        confirmLoading={editSaving}
        okText="Save changes"
        destroyOnClose
        width={520}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Manual override"
          description="Setting the status or dates by hand bypasses the payment gateway. For a gateway subscription a later webhook may overwrite these values. The change is recorded in the subscription history."
        />
        <Form form={editForm} layout="vertical">
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select options={STATUS_OPTIONS} placeholder="Select a status" />
          </Form.Item>
          <Form.Item name="start_date" label="Start date">
            <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" allowClear />
          </Form.Item>
          <Form.Item name="renewal_date" label="Renewal date">
            <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" allowClear />
          </Form.Item>
        </Form>
      </Modal>

      {/* Refund a subscription payment */}
      <Modal
        title="Refund subscription"
        open={!!refundModal}
        onCancel={() => setRefundModal(null)}
        onOk={handleRefundSave}
        confirmLoading={refundSaving}
        okText="Process refund"
        okButtonProps={{ danger: true }}
        destroyOnClose
        width={520}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="This issues a real refund"
          description={
            refundModal?.provider && refundModal.provider !== "OFFLINE"
              ? `The amount is refunded through ${refundModal.provider}, a credit note is issued, and the subscription is updated. Leave the amount blank for a full refund of the latest payment.`
              : "A credit note is issued and the subscription is updated. Leave the amount blank for a full refund of the latest payment."
          }
        />
        <Form form={refundForm} layout="vertical">
          <Form.Item name="amount" label="Amount (blank = full refund of the latest payment)">
            <Input type="number" min={0} step="0.01" placeholder="e.g. 500.00" />
          </Form.Item>
          <Form.Item name="reason" label="Reason">
            <Input.TextArea rows={2} placeholder="Why is this being refunded?" />
          </Form.Item>
          <Form.Item
            name="cancel_subscription"
            label="Cancel the subscription"
            valuePropName="checked"
            tooltip="Default: a full refund cancels the subscription, a partial one leaves it active. Toggle to override."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Upgrade plan (keep subscription — debit note for the prorated difference) */}
      <Modal
        title="Upgrade plan"
        open={!!inPlaceModal}
        onCancel={() => { setInPlaceModal(null); setInPlaceResult(null); }}
        onOk={inPlaceResult ? () => { setInPlaceModal(null); setInPlaceResult(null); } : handleInPlaceUpgrade}
        confirmLoading={inPlaceSubmitting}
        okText={inPlaceResult ? "Done" : "Upgrade"}
        cancelButtonProps={{ style: inPlaceResult ? { display: "none" } : {} }}
        destroyOnClose
        width={520}
      >
        {!inPlaceResult ? (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Keeps the same subscription"
              description="The plan changes immediately and the customer is billed a debit note for the prorated difference for the rest of the current cycle. The renewal date is unchanged; the new price applies in full from the next cycle."
            />
            <Form form={inPlaceForm} layout="vertical">
              <Form.Item name="plan" label="Upgrade to plan" rules={[{ required: true }]}>
                <Select showSearch optionFilterProp="label" options={planOptions} placeholder="Select a higher plan" />
              </Form.Item>
              <Form.Item name="offer_code" label="Offer code (optional)">
                <Input placeholder="Apply an offer to the new plan" />
              </Form.Item>
            </Form>
          </>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Alert type="success" showIcon message="Subscription upgraded" />
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Prorated difference">
                {inPlaceResult.quote?.difference ?? "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Debit note">
                {inPlaceResult.debit_note || "—"}
              </Descriptions.Item>
              {inPlaceResult.checkout?.short_url ? (
                <Descriptions.Item label="Collect difference">
                  <a href={inPlaceResult.checkout.short_url} target="_blank" rel="noreferrer">
                    Payment link
                  </a>
                </Descriptions.Item>
              ) : null}
            </Descriptions>
          </Space>
        )}
      </Modal>

      {/* Record an out-of-band gateway payment by its id */}
      <Modal
        title="Record gateway payment"
        open={!!recordPayModal}
        onCancel={() => setRecordPayModal(null)}
        onOk={handleRecordPayment}
        confirmLoading={recordPaySaving}
        okText="Record payment"
        destroyOnClose
        width={520}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Reconcile a payment made outside the subscription"
          description="Paste the gateway payment id (e.g. a payment link you sent that the customer paid). We verify it at the gateway, mark the matching invoice paid, and record the payment against this subscription."
        />
        <Form form={recordPayForm} layout="vertical">
          <Form.Item
            name="payment_id"
            label="Gateway payment id"
            rules={[{ required: true, message: "Enter the gateway payment id (e.g. pay_...)" }]}
          >
            <Input placeholder="pay_XXXXXXXXXXXX" />
          </Form.Item>
          <Form.Item name="amount" label="Amount (optional — defaults to the gateway/invoice amount)">
            <Input type="number" min={0} step="0.01" placeholder="e.g. 4500.00" />
          </Form.Item>
          <Form.Item
            name="mark_active"
            label="Activate the subscription if it is pending"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Attach an existing credit / debit note by id */}
      <Modal
        title={`Attach ${noteAttach?.type === "credit_note" ? "credit note" : "debit note"}`}
        open={!!noteAttach}
        onCancel={() => setNoteAttach(null)}
        onOk={handleNoteAttach}
        confirmLoading={noteAttaching}
        okText="Attach"
        destroyOnClose
      >
        <Form form={noteAttachForm} layout="vertical">
          <Form.Item
            name="note_id"
            label={`${noteAttach?.type === "credit_note" ? "Credit note" : "Debit note"} ID`}
            rules={[{ required: true, message: "Enter the document UUID" }]}
          >
            <Input placeholder="Document UUID" />
          </Form.Item>
          <Text type="secondary">
            The {noteAttach?.type === "credit_note" ? "credit note" : "debit note"} must belong to the
            same organization. It will be linked to this subscription.
          </Text>
        </Form>
      </Modal>

      {/* Attach an existing payment to one of the subscription's invoices */}
      <Modal
        title="Attach payment"
        open={paymentAttachOpen}
        onCancel={() => setPaymentAttachOpen(false)}
        onOk={handlePaymentAttach}
        confirmLoading={paymentAttaching}
        okText="Attach"
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Payments link to a subscription through an invoice. Pick which of this subscription's invoices the payment settles."
        />
        <Form form={paymentAttachForm} layout="vertical">
          <Form.Item name="payment_id" label="Payment ID"
            rules={[{ required: true, message: "Enter the payment UUID" }]}>
            <Input placeholder="Payment UUID" />
          </Form.Item>
          <Form.Item name="invoice_id" label="Attach to invoice"
            rules={[{ required: true, message: "Select an invoice" }]}>
            <Select
              placeholder={detailInvoices.length ? "Select an invoice" : "No invoices on this subscription"}
              options={detailInvoices.map((i) => ({ value: i.id, label: `${i.inv_number} — ${i.total}` }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default BillingSubscriptions;
